import React, { createRef } from 'react';
import { string } from 'prop-types';
import {
  InputGroup,
  FormControl,
  Form,
  FormCheck,
  FormSelect,
} from 'react-bootstrap';
import { useField, ErrorMessage } from 'formik';

import { smartFieldInputType } from './types';

import './SmartForm.scss';

function RegularInput({ name, input, submitForm, validateForm, autosave }) {
  const [field, meta] = useField(name);
  const { label, prepend, append, options, ...rest } = input;

  const onBlur = () => {
    if (!options && autosave)
      (async () => {
        await validateForm();
        await submitForm();
      })();
  };
  const onChange = () => {
    if (options && autosave)
      (async () => {
        await validateForm();
        await submitForm();
      })();
  };
  return (
    <Form.Group
      onBlur={onBlur}
      onChange={onChange}
      className={rest.className}
      size="lg"
    >
      {label && <Form.Label>{label}</Form.Label>}
      <InputGroup>
        {prepend && (
          <InputGroup.Prepend>
            <InputGroup.Text>{prepend}</InputGroup.Text>
          </InputGroup.Prepend>
        )}
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        {options && options.length ? (
          <FormSelect {...field} {...rest}>
            {options.map((option, index) => {
              // eslint-disable-next-line no-param-reassign
              option = ['string', 'number'].includes(typeof option)
                ? { value: option, name: option, label: option }
                : option;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <option key={`${option.label}-${index}`} value={option.value}>
                  {option.name}
                </option>
              );
            })}
          </FormSelect>
        ) : (
          <FormControl {...field} {...rest} />
        )}
        {append && (
          <InputGroup.Append>
            <InputGroup.Text>{append}</InputGroup.Text>
          </InputGroup.Append>
        )}
      </InputGroup>
      {meta.touched && meta.error ? (
        <Form.Text className="inline-error-message">{meta.error}</Form.Text>
      ) : null}
    </Form.Group>
  );
}

export function CheckBlock({
  isInvalid,
  checked,
  onClick,
  label,
  type,
  name,
  ...rest
}) {
  const newRef = createRef();
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormCheck custom type={type} {...rest}>
      <FormCheck.Input
        ref={newRef}
        name={name}
        isInvalid={isInvalid}
        checked={!!checked}
        value={checked}
        onClick={onClick}
        onChange={() => {}}
      />
      <FormCheck.Label onClick={() => newRef.current.click()}>
        {label}
      </FormCheck.Label>
    </FormCheck>
  );
}

function Check({ name, input, submitForm, validateForm, autosave, ...props }) {
  const [field, meta] = useField(name);
  const { setFieldValue } = props;

  const toggleCheck = async () => {
    setFieldValue(field.name, !field.value);
    if (autosave) {
      await validateForm();
      await submitForm();
    }
  };
  const selectRadio = async (value) => {
    setFieldValue(field.name, value);
    if (autosave) {
      await validateForm();
      await submitForm();
    }
  };
  const { options, label, ...rest } = input;
  return (
    <Form.Group className={input.className}>
      {options && options.length ? (
        options.map((option, index) => {
          // eslint-disable-next-line no-param-reassign
          option = ['string', 'number'].includes(typeof option)
            ? { value: option, label: option }
            : option;
          return (
            <CheckBlock
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...rest}
              /* eslint-disable-next-line react/no-array-index-key */
              key={`${option.value}-${index}`}
              isInvalid={meta.touched && meta.error}
              checked={field.value === option.value}
              onClick={() => selectRadio(option.value)}
              label={option.label}
            />
          );
        })
      ) : (
        <CheckBlock
          isInvalid={meta.touched && meta.error}
          checked={!!field.value}
          onClick={toggleCheck}
          label={label}
        />
      )}
      <ErrorMessage name={name}>Required</ErrorMessage>
    </Form.Group>
  );
}

function SmartField(props) {
  return ['radio', 'checkbox', 'switch'].includes(props.input.type) ? (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Check {...props} />
  ) : (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <RegularInput {...props} />
  );
}

SmartField.propTypes = {
  name: string.isRequired,
  input: smartFieldInputType.isRequired,
};

export default SmartField;
